// frontend/admin-portal/src/components/products/ProductImagesInline.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spinner } from "@fiberhome/shared-ui";
import { fileService, resolveImageUrl } from "../../services/fileService";

/**
 * Compact image picker for the ProductModal.
 * - Drop files anywhere on the dropzone
 * - Click to browse
 * - First image is the cover
 * - Reorder via ← / → buttons on each thumbnail
 * - Remove via ×
 *
 * value: string[]  (array of URLs)
 * onChange: (urls: string[]) => void
 */
export default function ProductImagesInline({
  value = [],
  onChange,
  entityType = "products",
  entityId = null,
  maxImages = 10,
  maxSizeMB = 10,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [localUrls, setLocalUrls] = useState(() =>
    Array.isArray(value) ? value : [],
  );

  // Keep internal state in sync if the parent replaces the value (e.g. when editing a different product)
  useEffect(() => {
    setLocalUrls(Array.isArray(value) ? value : []);
  }, [value]);

  const persist = useCallback(
    (next) => {
      setLocalUrls(next);
      onChange?.(next);
    },
    [onChange],
  );

  const validate = (files) => {
    const arr = Array.from(files);
    if (localUrls.length + arr.length > maxImages) {
      return { ok: false, message: `Max ${maxImages} images` };
    }
    for (const f of arr) {
      if (!f.type.startsWith("image/"))
        return { ok: false, message: `${f.name} is not an image` };
      if (f.size > maxSizeMB * 1024 * 1024)
        return {
          ok: false,
          message: `${f.name} is too large (max ${maxSizeMB} MB)`,
        };
    }
    return { ok: true, files: arr };
  };

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const check = validate(fileList);
    if (!check.ok) {
      setError(check.message);
      return;
    }

    setBusy(true);
    setProgress(0);
    setError("");

    try {
      const uploaded = await fileService.uploadImages(check.files, {
        entityType,
        entityId,
        onProgress: setProgress,
      });
      const urls = uploaded.map((f) => f.url).filter(Boolean);

      // Attach the first uploaded file to the product (best-effort)
      if (uploaded[0]?.id && entityId) {
        fileService
          .attach(uploaded[0].id, { entityType, entityId })
          .catch(() => {});
      }

      persist([...localUrls, ...urls]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Upload failed");
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    const next = [...localUrls];
    next.splice(idx, 1);
    persist(next);
  };

  const move = (from, to) => {
    if (to < 0 || to >= localUrls.length) return;
    const next = [...localUrls];
    const [picked] = next.splice(from, 1);
    next.splice(to, 0, picked);
    persist(next);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="fh-prod-img">
      <div className="fh-prod-img__head">
        <label className="fh-input-label mb-0">
          Photos ({localUrls.length})
        </label>
        {localUrls.length > 0 && (
          <span className="text-muted" style={{ fontSize: 11.5 }}>
            First image is the cover · reorder with ← →
          </span>
        )}
      </div>

      {error && (
        <div className="fh-prod-img__error" role="alert">
          {error}
        </div>
      )}

      {/* Thumbnail strip */}
      {localUrls.length > 0 && (
        <div className="fh-prod-img__strip">
          {localUrls.map((url, idx) => (
            <div
              key={url + idx}
              className={`fh-prod-img__thumb ${idx === 0 ? "is-cover" : ""}`}
            >
              <img src={resolveImageUrl(url)} alt={`image ${idx + 1}`} />

              {idx === 0 && <span className="fh-prod-img__badge">Cover</span>}

              <div className="fh-prod-img__overlay">
                {idx > 0 && (
                  <button
                    type="button"
                    className="fh-prod-img__nav"
                    onClick={() => move(idx, idx - 1)}
                    title="Move left"
                    aria-label="Move left"
                  >
                    ←
                  </button>
                )}
                <button
                  type="button"
                  className="fh-prod-img__remove"
                  onClick={() => remove(idx)}
                  title="Remove"
                  aria-label="Remove"
                >
                  ×
                </button>
                {idx < localUrls.length - 1 && (
                  <button
                    type="button"
                    className="fh-prod-img__nav"
                    onClick={() => move(idx, idx + 1)}
                    title="Move right"
                    aria-label="Move right"
                  >
                    →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      {localUrls.length < maxImages && (
        <div
          className={`fh-prod-img__dropzone ${dragging ? "is-over" : ""} ${busy ? "is-busy" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !busy && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleFiles(e.target.files)}
            disabled={busy}
          />

          {busy ? (
            <div className="fh-prod-img__busy">
              <Spinner size="md" />
              <span>
                Uploading{" "}
                {progress > 0 && progress < 100 ? `${progress}%` : "…"}
              </span>
            </div>
          ) : (
            <>
              <div className="fh-prod-img__icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <div className="fh-prod-img__text">
                {localUrls.length === 0
                  ? "Drag & drop photos or click to browse"
                  : "Add more photos"}
              </div>
              <div className="fh-prod-img__hint">
                Up to {maxImages} images · max {maxSizeMB} MB each · JPG / PNG /
                WebP
              </div>
            </>
          )}
        </div>
      )}

      {localUrls.length >= maxImages && (
        <div className="fh-prod-img__hint text-muted mt-1">
          Maximum of {maxImages} images reached.
        </div>
      )}
    </div>
  );
}
